/**
 * Pure form-validation rule functions.
 *
 * These functions are the single source of truth for what constitutes an invalid
 * customization configuration on the client. Each rule is a pure function over the
 * relevant entity inputs that returns the errors it found. Rules are slice-agnostic —
 * they accept minimal structural inputs so they can be reused by Contour+/Dose+/Image+/Adapt+.
 *
 * Each slice assembles its `selectFormValidationErrors` by memoising the relevant aggregators
 * from `selectors.ts` around its own entity-adapter input selectors.
 *
 * Adding a new rule
 * -----------------
 * 1. Add a new `FormValidationErrorType` entry in `src/store/global-types/form-errors.ts`
 *    and its i18n message in the same file.
 * 2. Register its severity in `SEVERITY_BY_ERROR_TYPE` below.
 * 3. Export a new `check<...>` function here with a JSDoc block covering:
 *    - what it checks
 *    - which `errorType` it emits
 *    - severity
 *    - scope (per-entity / per-output / per-customization-base / per-slice)
 *    - any suppression behaviour (e.g. empty-name error suppresses duplicate-name error)
 * 4. Cover it in `rules.test.ts`.
 * 5. Compose it from the appropriate aggregator in `selectors.ts`.
 */
import { CustomizationObjectType } from "../customization-types";
import { FormValidationErrorType } from "../form-errors";
import { FormValidationError, FormValidationErrorSeverity } from "../store-errors";


/** Severity by error type. Every known error type must have an entry here. */
const SEVERITY_BY_ERROR_TYPE: Record<FormValidationErrorType, FormValidationErrorSeverity> = {
    [FormValidationErrorType.Unspecified]: FormValidationErrorSeverity.Error,
    [FormValidationErrorType.ContourPlusRoiNameIsEmpty]: FormValidationErrorSeverity.Warning,
    [FormValidationErrorType.ContourPlusDuplicateRoiNamesInOutput]: FormValidationErrorSeverity.Warning,
    [FormValidationErrorType.DicomAttributesMustNotRepeat]: FormValidationErrorSeverity.Error,
    [FormValidationErrorType.DicomAttributeNameMustNotBeEmpty]: FormValidationErrorSeverity.Error,
    [FormValidationErrorType.DicomRuleMustHaveAtLeastOneAttribute]: FormValidationErrorSeverity.Error,
    [FormValidationErrorType.AeTitlesMustBeUnique]: FormValidationErrorSeverity.Error,
    [FormValidationErrorType.DosePlusRingDistancesNotAscending]: FormValidationErrorSeverity.Error,
    [FormValidationErrorType.DosePlusRingDistancesDuplicate]: FormValidationErrorSeverity.Error,
};

/** Returns the canonical severity for a given error type. */
export const severityForErrorType = (errorType: FormValidationErrorType): FormValidationErrorSeverity =>
    SEVERITY_BY_ERROR_TYPE[errorType];

/**
 * Builds a stable, deterministic id for a derived form validation error.
 * The id is `${itemId}:${errorType}` — stable across selector recomputations and usable
 * as a React key or lookup key without reference equality concerns.
 */
export const makeFormValidationErrorId = (itemId: string, errorType: FormValidationErrorType): string =>
    `${itemId}:${errorType}`;

/** Builds a `FormValidationError` with a stable id and the canonical severity for its errorType. */
export const makeError = (
    errorType: FormValidationErrorType,
    itemId: string,
    itemType: CustomizationObjectType,
): FormValidationError => ({
    id: makeFormValidationErrorId(itemId, errorType),
    itemId,
    itemType,
    errorType,
    severity: severityForErrorType(errorType),
});


// ---------------------------------------------------------------------------
// Structural input shapes
// ---------------------------------------------------------------------------
// Rules accept minimal structural inputs to stay slice-agnostic. Each slice's
// entity types extend these shapes, so entities can be passed directly.

export interface DicomRuleShape {
    id: string;
    dicomAttributes: string[];
}

export interface DicomAttributeShape {
    id: string;
    attribute: string;
}

export interface AeTitleShape {
    id: string;
    action: string;
}

export interface RoiShape {
    id: string;
    name: string;
    /** Defaults to true when omitted. Duplicate-name checks only consider included ROIs. */
    isIncluded?: boolean;
}


// ---------------------------------------------------------------------------
// Selection trigger rules (shared across all four slices)
// ---------------------------------------------------------------------------

/**
 * Checks that a DICOM rule has at least one DICOM attribute rule under it.
 *
 * - **Emits:** `DicomRuleMustHaveAtLeastOneAttribute` (severity: Error) keyed to the DICOM rule id.
 * - **Scope:** per-rule.
 */
export const checkDicomRuleHasAtLeastOneAttribute = (rule: DicomRuleShape): FormValidationError[] => {
    if (rule.dicomAttributes.length === 0) {
        return [makeError(FormValidationErrorType.DicomRuleMustHaveAtLeastOneAttribute, rule.id, CustomizationObjectType.DicomRule)];
    }
    return [];
};

/**
 * Checks the DICOM attributes of a single DICOM rule for empty names and duplicate names.
 *
 * - **Empty name:** emits `DicomAttributeNameMustNotBeEmpty` (Error) keyed to the attribute id.
 *   A name is empty if it is `undefined` or trims to an empty string.
 * - **Duplicate name:** emits `DicomAttributesMustNotRepeat` (Error) keyed to every attribute id
 *   that shares a name with another attribute in the same rule.
 * - **Suppression:** if an attribute has an empty name, it is NOT also flagged for duplicate-name —
 *   empty dominates.
 * - **Scope:** per-rule (inspects the rule's own attributes; does not cross rule boundaries).
 *
 * Callers should build `attributesById` once for the slice and reuse it across all rules so the
 * per-rule check is O(K) in attributes-of-this-rule rather than O(M) in total slice attributes.
 */
export const checkDicomAttributesInRule = (
    rule: DicomRuleShape,
    attributesById: ReadonlyMap<string, DicomAttributeShape>,
): FormValidationError[] => {
    const attrsInRule: DicomAttributeShape[] = [];
    for (const id of rule.dicomAttributes) {
        const attr = attributesById.get(id);
        if (attr !== undefined) { attrsInRule.push(attr); }
    }

    // Group by trimmed attribute name to match save-time semantics — `view-model-conversion-helpers.ts`
    // serialises `dicomAttribute.attribute.trim()` as the JSON key, so two attributes that differ only
    // in surrounding whitespace collapse to the same key on save and must be flagged here too.
    const idsByName = new Map<string, string[]>();
    for (const attr of attrsInRule) {
        const key = attr.attribute?.trim() ?? '';
        const existing = idsByName.get(key);
        if (existing !== undefined) { existing.push(attr.id); }
        else { idsByName.set(key, [attr.id]); }
    }

    const errors: FormValidationError[] = [];
    for (const attr of attrsInRule) {
        const trimmedName = attr.attribute?.trim() ?? '';
        const isEmpty = trimmedName === '';
        if (isEmpty) {
            // empty dominates duplicate — emit only the empty-name error
            errors.push(makeError(FormValidationErrorType.DicomAttributeNameMustNotBeEmpty, attr.id, CustomizationObjectType.DicomAttributeRule));
            continue;
        }
        const group = idsByName.get(trimmedName);
        if (group !== undefined && group.length > 1) {
            errors.push(makeError(FormValidationErrorType.DicomAttributesMustNotRepeat, attr.id, CustomizationObjectType.DicomAttributeRule));
        }
    }
    return errors;
};

/**
 * Checks that no two AE Title rules share an `action` value. Rules with a shared action are all flagged.
 *
 * - **Emits:** `AeTitlesMustBeUnique` (Error) keyed to every AE title rule id that participates in a duplicate group.
 * - **Scope:** per-slice. Uniqueness is checked across every AE title rule in the slice regardless of
 *   which customization base / model they belong to — two different Contour+ models with the same AE
 *   title action collide, and both rules are flagged.
 */
export const checkAeTitleUniqueness = (rules: AeTitleShape[]): FormValidationError[] => {
    // Group by trimmed action to match save-time semantics — `view-model-conversion-helpers.ts`
    // serialises `aeTitleRule.action.trim()`, so two rules that differ only in surrounding whitespace
    // collide on the backend even though the editor would otherwise see them as unique.
    const idsByAction = new Map<string, string[]>();
    for (const rule of rules) {
        const key = rule.action.trim();
        const existing = idsByAction.get(key);
        if (existing !== undefined) { existing.push(rule.id); }
        else { idsByAction.set(key, [rule.id]); }
    }

    const errors: FormValidationError[] = [];
    for (const ids of idsByAction.values()) {
        if (ids.length > 1) {
            for (const id of ids) {
                errors.push(makeError(FormValidationErrorType.AeTitlesMustBeUnique, id, CustomizationObjectType.AeTitleRule));
            }
        }
    }
    return errors;
};


// ---------------------------------------------------------------------------
// Contour+ output rules
// ---------------------------------------------------------------------------

/**
 * Checks the ROIs in a single Contour+ customization output for empty names and duplicate names.
 *
 * - **Empty name:** emits `ContourPlusRoiNameIsEmpty` (Warning) keyed to the ROI id. A name is empty if it
 *   trims to an empty string.
 * - **Duplicate name:** emits `ContourPlusDuplicateRoiNamesInOutput` (Warning) keyed to every included ROI id
 *   whose name matches another included ROI's name within the same output.
 * - **Scope:** per-output. `rois` must be the full list of ROIs in one customization output.
 * - **Suppression rules:**
 *     1. Empty-name ROIs are NOT flagged for duplicate-name (empty dominates).
 *     2. Excluded ROIs (`isIncluded === false`) are NOT considered when detecting duplicates,
 *        and are NOT flagged for duplicate-name themselves.
 *     3. Excluded ROIs ARE still flagged for empty-name — keeps the warning visible on the row
 *        the user is editing even if they temporarily un-include it.
 */
export const checkRoiNamesInOutput = (rois: RoiShape[]): FormValidationError[] => {
    // Group included-ROI ids by trimmed name to match save-time semantics — `contouring-helpers.ts`
    // serialises `roi.name.trim()`, so two ROIs that differ only in surrounding whitespace collapse
    // to the same name on save and must be flagged as duplicates here too.
    const includedIdsByName = new Map<string, string[]>();
    for (const roi of rois) {
        const isIncluded = roi.isIncluded !== false;
        if (!isIncluded) { continue; }
        const trimmedName = roi.name.trim();
        const existing = includedIdsByName.get(trimmedName);
        if (existing !== undefined) { existing.push(roi.id); }
        else { includedIdsByName.set(trimmedName, [roi.id]); }
    }

    const errors: FormValidationError[] = [];
    for (const roi of rois) {
        const isIncluded = roi.isIncluded !== false;
        const trimmedName = roi.name.trim();
        const isEmpty = !trimmedName;

        if (isEmpty) {
            errors.push(makeError(FormValidationErrorType.ContourPlusRoiNameIsEmpty, roi.id, CustomizationObjectType.Roi));
            continue; // empty dominates duplicate
        }

        if (!isIncluded) { continue; } // excluded ROIs do not participate in duplicate detection

        const group = includedIdsByName.get(trimmedName);
        if (group !== undefined && group.length > 1) {
            errors.push(makeError(FormValidationErrorType.ContourPlusDuplicateRoiNamesInOutput, roi.id, CustomizationObjectType.Roi));
        }
    }
    return errors;
};


// ---------------------------------------------------------------------------
// Dose+ output rules
// ---------------------------------------------------------------------------

/**
 * Checks Dose+ optimization ring distances on a single customization output.
 *
 * - **Emits one of:**
 *     - `DosePlusRingDistancesDuplicate` (Error) if two distances round-compare equal.
 *     - `DosePlusRingDistancesNotAscending` (Error) if distances are not strictly ascending
 *       and there are NO duplicates. Duplicate detection dominates; when both would fire, only the
 *       duplicate error is emitted.
 * - **Scope:** per-output. Keyed to the output id (`CustomizationObjectType.CustomizationOutput`).
 * - **Gating:** if `isRoiCreationEnabled` is false, no errors are emitted regardless of distance values.
 * - **Precision:** distances are rounded to 4 decimal places before comparison to avoid
 *   floating-point precision artefacts.
 */
export const checkDoseRingDistances = (
    outputId: string,
    isRoiCreationEnabled: boolean,
    distances: number[],
): FormValidationError[] => {
    if (!isRoiCreationEnabled) { return []; }

    const rounded = distances.map(d => parseFloat(d.toFixed(4)));
    const hasDuplicates = new Set(rounded).size !== rounded.length;
    if (hasDuplicates) {
        return [makeError(FormValidationErrorType.DosePlusRingDistancesDuplicate, outputId, CustomizationObjectType.CustomizationOutput)];
    }

    const isAscending = rounded.every((d, i) => i === 0 || rounded[i - 1] < d);
    if (!isAscending) {
        return [makeError(FormValidationErrorType.DosePlusRingDistancesNotAscending, outputId, CustomizationObjectType.CustomizationOutput)];
    }

    return [];
};
